import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { HOLIDAYS, COUNTRY_FLAGS } from '../../types/holidays';
import { adjustDay, getDaysInMonth } from '../../utils/date';
import { getMonthName, getWeekdayLabels, t } from '../../i18n';
import type { CalendarSettings, CountryCode } from '../../types/common';
import type { Holiday } from '../../types/holidays';

@customElement('printer-monthly')
export class PrinterMonthly extends LitElement {
  @property({ type: Number }) year = new Date().getFullYear();
  @property({ type: Number }) month = new Date().getMonth();
  @property({ type: String }) monthName = '';
  @property({ type: Object }) settings: CalendarSettings = {
    borderWidth: 1,
    gridColor: '#999999',
    gridOpacity: 100,
    fontColor: '#333333',
    fontOpacity: 100,
    weekStart: 'Sunday',
    country: '',
    holidayColor: '#FF0000',
    holidayOpacity: 5,
    weekendColor: '#FF0000',
    weekendOpacity: 5,
    showSidebar: false,
    showFootnotes: false,
    gridStyle: 'Rectangle'
  };

  @state() private isConfigPanelOpen = false;
  @state() private dayContents: Map<number, string> = new Map();
  @state() private sidebarContent: string = '';
  @state() private footnotesContent: string = '';
  @state() private placeholdersInitialized = false;

  static styles = css`
    :host {
      display: block;
      --font-family: var(--font-family, 'Inter', sans-serif);
      --primary-color: var(--primary-color, #2563eb);
      --text-color: var(--text-color, #1f2937);
      --font-weight-normal: var(--font-weight-normal, 400);
      --font-weight-semibold: var(--font-weight-semibold, 600);
      --font-weight-bold: var(--font-weight-bold, 700);
      
      /* Button style variables */
      --button-background: var(--primary-color, #2563eb);
      --button-color: white;
      --button-padding: 8px 16px;
      --button-border: none;
      --button-border-radius: 4px;
      --button-font-weight: var(--font-weight-semibold, 600);
      --button-hover-brightness: 0.9;
      --button-min-width: 120px;
    }

    /* Calendar container styles */
    .calendar-container {
      width: 100%;
      margin: 0;
      font-family: var(--font-family);
    }

    /* Calendar controls styles */
    .calendar-controls {
      display: flex;
      justify-content: space-between;
      margin: 2rem auto 0;
      max-width: 600px;
      width: 100%;
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .action-button {
      background-color: var(--button-background);
      color: var(--button-color);
      border: var(--button-border);
      border-radius: var(--button-border-radius);
      padding: var(--button-padding);
      font-family: var(--font-family);
      font-weight: var(--button-font-weight);
      cursor: pointer;
      display: inline-block;
      text-align: center;
      min-width: var(--button-min-width);
      box-sizing: border-box;
      margin: 5px;
      line-height: 1.5;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      font-size: 1.1rem;
      flex: 1;
    }

    .action-button:hover {
      filter: brightness(var(--button-hover-brightness));
    }

    .bottom-controls {
      margin-top: 2rem;
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 30px auto;
      gap: 10px;
      width: 100%;
      max-width: 500px;
    }

    /* Header row styles */
    .header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
      padding-top: 1rem;
    }

    .month-year {
      text-align: center;
      font-size: 1.5rem;
      font-weight: bold;
    }

    .brand {
      font-family: var(--font-family);
      font-size: 18px;
      font-weight: var(--font-weight-bold);
      color: var(--primary-color);
      display: none;
    }

    /* Configuration panel styles */
    .config-panel {
      position: relative;
      background: white;
      border: 2px solid #ddd;
      border-radius: 4px;
      padding: 1rem;
      margin-top: 1rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      max-width: 800px;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 2rem;
    }

    .config-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;
    }

    .config-group {
      border: 2px solid #eee;
      border-radius: 4px;
      padding: 1rem;
      margin-bottom: 1rem;
      background: #f5f5f5;
      position: relative;
    }

    .config-group:last-child {
      margin-bottom: 0;
    }

    .config-group h3 {
      position: absolute;
      top: -0.75rem;
      left: 0.75rem;
      margin: 0;
      padding: 0 0.5rem;
      font-size: 0.9rem;
      color: var(--text-color);
      background: #f5f5f5;
    }

    .config-row {
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .config-row label {
      font-size: 0.9rem;
      font-weight: normal;
    }

    .config-row select {
      width: 120px;
      font-family: 'Inter', sans-serif;
      padding: 0.5rem;
      border-radius: 4px;
      border: 2px solid #ddd;
      background-color: white;
    }

    .config-row input[type="number"] {
      font-family: 'Inter', sans-serif;
      padding: 0.5rem;
      border-radius: 4px;
      border: 2px solid #ddd;
    }

    .color-with-opacity {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .color-with-opacity input[type="color"] {
      width: 30px;
      height: 30px;
      padding: 0;
      border: 2px solid #ddd;
      border-radius: 4px;
    }

    .color-with-opacity input[type="range"] {
      width: 100px;
      margin: 0;
    }

    .opacity-value {
      min-width: 3em;
      text-align: right;
      font-size: 0.8rem;
      color: #666;
    }

    /* Calendar layout styles */
    .layout-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 5px;
      table-layout: fixed;
      height: 100%;
    }

    .main-calendar-cell {
      width: 80%;
      vertical-align: top;
      padding: 0;
      height: 100%;
    }

    .sidebar-cell {
      width: 20%;
      vertical-align: top;
      padding: 0;
      border: 0;
      height: 100%;
      box-sizing: border-box;
    }

    .footnotes-cell {
      vertical-align: top;
      padding: 0;
      border: 0;
      height: 80px;
      box-sizing: border-box;
    }

    /* Calendar table styles */
    .weekday-table,
    .days-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
    }

    .weekday-table {
      margin-bottom: 0;
    }

    .weekday-table th {
      padding: 0.25rem;
      text-align: center !important;
      font-weight: 600;
      width: 14.285%;
    }

    .days-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      table-layout: fixed;
      height: calc(100% - 30px);
    }

    .days-table td {
      border-width: var(--border-width, 1px);
      border-style: solid;
      border-color: var(--grid-color, #999);
      padding: 0;
      vertical-align: top;
      height: 100px;
      width: 14.285%;
      position: relative;
      overflow: hidden;
      padding-bottom: 1.5rem;
      print-color-adjust: exact;
    }

    .day-number {
      padding: 0.25rem;
      font-size: 1.1rem;
      font-weight: 400;
    }

    .day-content {
      min-height: 50px;
      padding: 0;
      font-size: 0.8rem;
      position: absolute;
      top: 1.75rem;
      left: 0.25rem;
      right: 0.25rem;
      bottom: 1.5rem;
      border: none;
      resize: none;
      background: transparent;
      overflow: hidden;
      /* Use ellipsis for overflowing text */
      text-overflow: ellipsis;
      font-family: var(--font-family);
    }

    .day-content:focus {
      background: #f7ffbe;
      outline: none;
    }

    .day-content:hover {
      background-color: rgba(247, 255, 190, 0.3);
    }
    
    /* Replace contenteditable styling with textarea styling */
    textarea {
      font-family: var(--font-family);
      border: none;
      resize: none;
      background: transparent;
      overflow: hidden;
      white-space: pre-wrap;
    }

    textarea:focus {
      background-color: #f7ffbe;
      outline: none;
    }

    textarea:hover {
      background-color: rgba(247, 255, 190, 0.3);
    }

    .calendar-day-other-month {
      color: transparent;
    }

    .calendar-day-other-month .day-number {
      visibility: hidden;
    }

    .holiday-label {
      padding: 0.5rem;
      font-size: 0.75rem;
      font-family: 'Noto Color Emoji', "Segoe UI Emoji", "Apple Color Emoji", sans-serif;
      opacity: 0.85;
      max-width: calc(100% - 0.5rem);
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      box-sizing: border-box;
      text-align: center;
    }

    .sidebar-content, 
    .footnotes-content {
      padding: 8px;
      color: var(--font-color, #333);
      font-size: 0.9rem;
      box-sizing: border-box;
      overflow: hidden;
      width: 100%;
      background: transparent;
    }
    
    .sidebar-content {
      height: 100%;
      min-height: 300px;
    }

    .footnotes-content {
      height: 100%;
      max-height: 100%;
      line-height: 1.2;
    }

    @media (max-width: 768px) {
      .config-grid {
        grid-template-columns: 1fr;
      }
    }

    @media print {
      .calendar-controls,
      .bottom-controls,
      .config-panel {
        display: none !important;
      }

      .brand {
        display: block !important;
        color: var(--primary-color) !important;
        font-weight: var(--font-weight-bold) !important;
        font-size: 18px !important;
        visibility: visible !important;
        font-family: var(--font-family) !important;
      }

      /* Fill page height when footnotes are off */
      .layout-table:not(:has(.footnotes-cell)) .days-table {
        height: 80vh !important;
      }

      .layout-table .days-table td {
        height: calc(70vh / 7) !important;
      }

      /* Mobile print optimizations */
      @page {
        size: 100%;
        margin: 0.5cm;
      }
      
      /* Make calendar more compact for mobile printing */
      .days-table td {
        height: auto !important; /* Let content determine height */
      }
    }

    /* Grid style variations */
    .grid-style-rounded .weekday-table {
      border-collapse: separate;
    }
    
    .grid-style-rounded .days-table {
      border-collapse: separate;
    }
    
    /* Round specific corners of the outer grid */
    .grid-style-rounded .days-table tr:first-child td:first-child {
      border-top-left-radius: 20px;
    }
    
    .grid-style-rounded .days-table tr:first-child td:last-child {
      border-top-right-radius: 20px;
    }
    
    .grid-style-rounded .days-table tr:last-child td:first-child {
      border-bottom-left-radius: 20px;
    }
    
    .grid-style-rounded .days-table tr:last-child td:last-child {
      border-bottom-right-radius: 20px;
    }

    .grid-style-minimal .days-table td {
      border-width: 0;
      position: relative;
    }

    .grid-style-minimal .days-table td::before {
      content: '';
      position: absolute;
      top: 0;
      left: 5%;
      width: 90%;
      height: var(--border-width, 1px);
      background-color: var(--grid-color, #999);
      opacity: var(--grid-opacity, 1);
    }
  `;

  connectedCallback() {
    super.connectedCallback();

    // Initialize placeholders if they haven't been set
    this.ensurePlaceholders();

    // If month name isn't set but month number is, set it automatically
    if (!this.monthName && this.month >= 0 && this.month <= 11) {
      this.monthName = getMonthName(this.month);
    }
    
    // Add viewport meta tag for better mobile printing
    this.addViewportMeta();
  }

  private ensurePlaceholders(): void {
    if (!this.placeholdersInitialized) {
      this.sidebarContent = t('printer.sideNotes');
      this.footnotesContent = t('printer.footnotes');
      this.placeholdersInitialized = true;
      return;
    }
    if (!this.sidebarContent) {
      this.sidebarContent = t('printer.sideNotes');
    }
    if (!this.footnotesContent) {
      this.footnotesContent = t('printer.footnotes');
    }
  }

  private toggleConfigPanel() {
    this.isConfigPanelOpen = !this.isConfigPanelOpen;
  }

  private handleSettingChange(e: Event) {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const id = target.id;
    const newSettings = { ...this.settings };
    
    if (id === 'gridStyle') {
      newSettings.gridStyle = target.value as 'Rectangle' | 'Rounded' | 'Minimal';
    } else if (id === 'borderWidth') {
      newSettings.borderWidth = Number(target.value);
    } else if (id === 'gridColor') {
      newSettings.gridColor = target.value;
    } else if (id === 'gridOpacity') {
      newSettings.gridOpacity = Number(target.value);
    } else if (id === 'fontColor') {
      newSettings.fontColor = target.value;
    } else if (id === 'fontOpacity') {
      newSettings.fontOpacity = Number(target.value);
    } else if (id === 'weekendColor') {
      newSettings.weekendColor = target.value;
    } else if (id === 'weekendOpacity') {
      newSettings.weekendOpacity = Number(target.value);
    } else if (id === 'holidayColor') {
      newSettings.holidayColor = target.value;
    } else if (id === 'holidayOpacity') {
      newSettings.holidayOpacity = Number(target.value);
    } else if (id === 'weekStart') {
      newSettings.weekStart = target.value as 'Sunday' | 'Monday';
    } else if (id === 'country') {
      newSettings.country = target.value as '' | CountryCode;
    } else if (id === 'showSidebar') {
      newSettings.showSidebar = (target as HTMLInputElement).checked;
    } else if (id === 'showFootnotes') {
      newSettings.showFootnotes = (target as HTMLInputElement).checked;
    }

    this.settings = newSettings;
    this.requestUpdate();
  }

  private handleTextInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    
    if (target.classList.contains('day-content')) {
      const cells = Array.from(this.shadowRoot!.querySelectorAll('.day-content'));
      const index = cells.indexOf(target);
      if (index !== -1) {
        this.dayContents.set(index, target.value);
      }
    } else if (target.classList.contains('sidebar-content')) {
      this.sidebarContent = target.value;
    } else if (target.classList.contains('footnotes-content')) {
      this.footnotesContent = target.value;
    }
  }

  generateCalendarGrid() {
    const firstDay = new Date(this.year, this.month, 1);
    const daysInMonth = getDaysInMonth(this.month, this.year);
    const startingDay = adjustDay(firstDay.getDay(), this.settings.weekStart === 'Monday');
    const weekdays = getWeekdayLabels(this.settings.weekStart === 'Monday');

    // Get holidays for selected country and year
    const holidays: Holiday[] = this.settings.country ?
      (HOLIDAYS[this.settings.country as CountryCode][this.year] || []).filter(h =>
        h.date.getMonth() === this.month
      ) : [];

    // Calculate colors with opacity
    const fontColorWithOpacity = this.settings.fontColor + Math.round(this.settings.fontOpacity * 2.55).toString(16).padStart(2, '0');
    const gridColorWithOpacity = this.settings.gridColor + Math.round(this.settings.gridOpacity * 2.55).toString(16).padStart(2, '0');

    // Create the calendar grid starting with the weekday headers
    const weekdayHeaders = weekdays.map(day => html`<th>${day}</th>`);

    // Build the days grid
    const rows = [];
    let currentDay = 1;
    const totalDays = startingDay + daysInMonth;
    const totalWeeks = Math.ceil(totalDays / 7);

    for (let week = 0; week < totalWeeks; week++) {
      const cells = [];
      for (let day = 0; day < 7; day++) {
        const dayIndex = week * 7 + day;
        const adjustedDay = this.settings.weekStart === 'Monday' ? (day + 1) % 7 : day;
        const isWeekend = adjustedDay === 0 || adjustedDay === 6;
        const isInMonth = dayIndex >= startingDay && currentDay <= daysInMonth;
        
        const holiday = isInMonth ? holidays.find(h => h.date.getDate() === currentDay) : null;
        
        // Apply background colors for weekends and holidays
        let background = '';
        if (holiday && this.settings.holidayColor) {
          const color = `${this.settings.holidayColor}${Math.round(this.settings.holidayOpacity * 2.55).toString(16).padStart(2, '0')}`;
          background = `background-color: ${color} !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;`;
        } else if (isWeekend && this.settings.weekendColor) {
          const color = `${this.settings.weekendColor}${Math.round(this.settings.weekendOpacity * 2.55).toString(16).padStart(2, '0')}`;
          background = `background-color: ${color} !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;`;
        }
        
        const dayClass = isInMonth ? '' : 'calendar-day-other-month';
        const dayNumber = isInMonth ? currentDay++ : '';
        const dayContent = this.dayContents.get(dayIndex) || '';
        const holidayDisplay = holiday ? 
          html`<div class="holiday-label">${COUNTRY_FLAGS[this.settings.country as CountryCode]} ${holiday.name}</div>` : 
          '';
        
        cells.push(html`
          <td class="${dayClass}" style="${background}">
            <div class="day-number">${dayNumber}</div>
            <textarea 
              class="day-content" 
              @input=${this.handleTextInput}
              .value=${dayContent}></textarea>
            ${holidayDisplay}
          </td>
        `);
      }
      rows.push(html`<tr>${cells}</tr>`);
    }

    // Return calendar grid with optional sidebar and footnotes
    return html`
      <table class="layout-table">
        <tr>
          <td class="main-calendar-cell">
            <table class="weekday-table" style="--font-color: ${fontColorWithOpacity}">
              <tr>${weekdayHeaders}</tr>
            </table>
            <table 
              class="days-table" 
              style="
                --border-width: ${this.settings.borderWidth}px;
                --grid-color: ${gridColorWithOpacity};
                --font-color: ${fontColorWithOpacity};
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;">
              <tbody>
                ${rows}
              </tbody>
            </table>
          </td>
          ${this.settings.showSidebar ? html`
            <td class="sidebar-cell" style="--grid-color: ${gridColorWithOpacity}; --font-color: ${fontColorWithOpacity};">
              <textarea 
                class="sidebar-content" 
                @input=${this.handleTextInput}
                .value=${this.sidebarContent}></textarea>
            </td>
          ` : ''}
        </tr>
        ${this.settings.showFootnotes ? html`
          <tr>
            <td class="footnotes-cell" colspan="${this.settings.showSidebar ? 2 : 1}" style="--grid-color: ${gridColorWithOpacity}; --font-color: ${fontColorWithOpacity};">
              <textarea 
                class="footnotes-content" 
                @input=${this.handleTextInput}
                .value=${this.footnotesContent}></textarea>
            </td>
          </tr>
        ` : ''}
      </table>
    `;
  }

  render() {
    const gridStyleClass = this.settings.gridStyle ? `grid-style-${this.settings.gridStyle.toLowerCase()}` : '';
    
    return html`
      <div class="calendar-container ${gridStyleClass}">
        <div class="calendar-controls">
          <button 
            @click=${this.toggleConfigPanel} 
            class="action-button">
            ${t('printer.configure')} ${this.isConfigPanelOpen ? '▲' : '▼'}
          </button>
          <button 
            @click=${() => window.print()} 
            class="action-button">
            ${t('printer.printCalendar')}
          </button>
        </div>
        
        ${this.isConfigPanelOpen ? html`
          <div id="configPanel" class="config-panel">
            <div class="config-grid">
              <div class="config-group">
                <h3>${t('printer.theme')}</h3>
                <div class="config-row">
                  <label for="gridStyle">${t('printer.gridStyle')}</label>
                  <select id="gridStyle" @change=${this.handleSettingChange}>
                    <option value="Rectangular" ?selected=${this.settings.gridStyle === 'Rectangle'}>${t('printer.gridRectangle')}</option>
                    <option value="Rounded" ?selected=${this.settings.gridStyle === 'Rounded'}>${t('printer.gridRounded')}</option>
                    <option value="Minimal" ?selected=${this.settings.gridStyle === 'Minimal'}>${t('printer.gridMinimal')}</option>
                  </select>
                </div>
                <div class="config-row">
                  <label for="borderWidth">${t('printer.lineWidth')}</label>
                  <input 
                    type="number" 
                    id="borderWidth"
                    value=${this.settings.borderWidth} 
                    min="0"
                    @change=${this.handleSettingChange}>
                </div>
                <div class="config-row">
                  <label>${t('printer.lineColor')}</label>
                  <div class="color-with-opacity">
                    <input 
                      type="color" 
                      id="gridColor" 
                      value=${this.settings.gridColor} 
                      @change=${this.handleSettingChange}>
                    <input 
                      type="range" 
                      id="gridOpacity" 
                      min="0" 
                      max="100" 
                      value=${this.settings.gridOpacity} 
                      @input=${this.handleSettingChange} 
                      @change=${this.handleSettingChange}>
                    <span class="opacity-value">${this.settings.gridOpacity}%</span>
                  </div>
                </div>
                <div class="config-row">
                  <label>${t('printer.fontColor')}</label>
                  <div class="color-with-opacity">
                    <input 
                      type="color" 
                      id="fontColor" 
                      value=${this.settings.fontColor} 
                      @change=${this.handleSettingChange}>
                    <input 
                      type="range" 
                      id="fontOpacity" 
                      min="0" 
                      max="100" 
                      value=${this.settings.fontOpacity} 
                      @input=${this.handleSettingChange} 
                      @change=${this.handleSettingChange}>
                    <span class="opacity-value">${this.settings.fontOpacity}%</span>
                  </div>
                </div>
                <div class="config-row">
                  <label>${t('printer.weekend')}</label>
                  <div class="color-with-opacity">
                    <input 
                      type="color" 
                      id="weekendColor" 
                      value=${this.settings.weekendColor || '#CC0000'} 
                      @change=${this.handleSettingChange}>
                    <input 
                      type="range" 
                      id="weekendOpacity" 
                      min="0" 
                      max="100" 
                      value=${this.settings.weekendOpacity} 
                      @input=${this.handleSettingChange} 
                      @change=${this.handleSettingChange}>
                    <span class="opacity-value">${this.settings.weekendOpacity}%</span>
                  </div>
                </div>
                <div class="config-row">
                  <label>${t('printer.holiday')}</label>
                  <div class="color-with-opacity">
                    <input 
                      type="color" 
                      id="holidayColor" 
                      value=${this.settings.holidayColor || '#CC0000'} 
                      @change=${this.handleSettingChange}>
                    <input 
                      type="range" 
                      id="holidayOpacity" 
                      min="0" 
                      max="100" 
                      value=${this.settings.holidayOpacity} 
                      @input=${this.handleSettingChange} 
                      @change=${this.handleSettingChange}>
                    <span class="opacity-value">${this.settings.holidayOpacity}%</span>
                  </div>
                </div>
              </div>
              <div class="config-group">
                <h3>${t('printer.grid')}</h3>
                <div class="config-row">
                  <label for="weekStart">${t('printer.weekStartsOn')}</label>
                  <select id="weekStart" @change=${this.handleSettingChange}>
                    <option value="Monday" ?selected=${this.settings.weekStart === 'Monday'}>${t('printer.monday')}</option>
                    <option value="Sunday" ?selected=${this.settings.weekStart === 'Sunday'}>${t('printer.sunday')}</option>
                  </select>
                </div>
                <div class="config-row">
                  <label for="country">${t('printer.showCountryHolidays')}</label>
                  <select id="country" @change=${this.handleSettingChange}>
                    <option value="" ?selected=${this.settings.country === ''}>${t('printer.none')}</option>
                    <option value="Canada" ?selected=${this.settings.country === 'Canada'}>🇨🇦 ${t('printer.canada')}</option>
                    <option value="Mexico" ?selected=${this.settings.country === 'Mexico'}>🇲🇽 ${t('printer.mexico')}</option>
                    <option value="UK" ?selected=${this.settings.country === 'UK'}>🇬🇧 ${t('printer.uk')}</option>
                    <option value="USA" ?selected=${this.settings.country === 'USA'}>🇺🇸 ${t('printer.usa')}</option>
                  </select>
                </div>
                <div class="config-row">
                  <label for="showSidebar">${t('printer.showSidebar')}</label>
                  <input 
                    type="checkbox" 
                    id="showSidebar"
                    ?checked=${this.settings.showSidebar} 
                    @change=${this.handleSettingChange}>
                </div>
                <div class="config-row">
                  <label for="showFootnotes">${t('printer.showFootnotes')}</label>
                  <input 
                    type="checkbox" 
                    id="showFootnotes" 
                    ?checked=${this.settings.showFootnotes} 
                    @change=${this.handleSettingChange}>
                </div>
              </div>
            </div>
          </div>
        ` : ''}
        
        <div class="header-row">
          <span class="month-year">${this.monthName} ${this.year}</span>
          <span class="brand">Calendarify</span>
        </div>
        
        <div id="calendar-grid-container">
          ${this.generateCalendarGrid()}
        </div>
        
        <div class="bottom-controls">
          <button @click=${() => window.print()} class="action-button">
            ${t('printer.printCalendar')}
          </button>
        </div>
      </div>
    `;
  }
  
  // Add method to handle viewport meta tag
  private addViewportMeta() {
    // Check if we need to add a viewport meta tag
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.setAttribute('name', 'viewport');
      document.head.appendChild(viewportMeta);
    }
    
    // Set the appropriate viewport content for better printing
    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    
    // Add the mobile-web-app-capable meta tag
    let mobileMeta = document.querySelector('meta[name="mobile-web-app-capable"]');
    if (!mobileMeta) {
      mobileMeta = document.createElement('meta');
      mobileMeta.setAttribute('name', 'mobile-web-app-capable');
      mobileMeta.setAttribute('content', 'yes');
      document.head.appendChild(mobileMeta);
    }
  }
}
